import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Phase 8 — `withCredentials: true` makes axios attach the
// `procurement_session` HttpOnly cookie on every request. Every page
// that talks to /api/* goes through this instance, so we never
// forget to send the cookie.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Extract a human-readable error string from any shape of API error.
// Supports:
//   - `detail: "<string>"`                    → return the string.
//   - `detail: {code, message, hint}` (old)   → return `message`.
//   - `detail: [{msg, loc, type, ...}]`       → FastAPI 422: return `msg` of first entry.
//   - `detail: <other object>`                → JSON.stringify fallback.
//   - no `detail`                             → fall back to `err.message` or default.
// MUST return a string. Passing an object to `toast.error()` triggers
// a React "Objects are not valid as a React child" crash which, without
// an ErrorBoundary around it, unmounts the whole page.
export const detailFromError = (e) => {
  const defaultMsg = "Request failed. Please try again.";
  const raw = e?.response?.data?.detail;

  if (raw == null) {
    return (e?.message && String(e.message)) || defaultMsg;
  }
  if (typeof raw === "string") return raw;

  // FastAPI 422 — Pydantic validation errors come as an array of
  // {loc, msg, type} objects. First msg is enough for a toast.
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (first && typeof first === "object") {
      const msg = first.msg || first.message || first.type;
      if (msg) return String(msg);
    }
    try {
      return JSON.stringify(raw);
    } catch {
      return defaultMsg;
    }
  }

  if (typeof raw === "object") {
    const picked = raw.message || raw.msg || raw.code || raw.detail;
    if (picked) return String(picked);
    try {
      return JSON.stringify(raw);
    } catch {
      return defaultMsg;
    }
  }

  return String(raw);
};

// Pull the stable machine code out of an API error (used for conditional UI
// handling like "cookies stale → open re-import dialog"). Returns null when
// no code is present (network error, non-structured response, etc).
export const errorCode = (e) => {
  const data = e?.response?.data;
  if (!data) return null;
  // Preferred: `error_meta.code` sibling JSON (new backend contract — Nov 2026).
  // Chose a JSON sibling (not a response header) because custom headers are
  // stripped under CORS unless whitelisted via Access-Control-Expose-Headers,
  // and because JSON is Unicode-safe (HTTP header values are latin-1 only).
  if (data.error_meta && typeof data.error_meta === "object" && typeof data.error_meta.code === "string") {
    return data.error_meta.code;
  }
  // Legacy top-level `code` sibling.
  if (typeof data.code === "string") return data.code;
  // Legacy inside structured detail object.
  if (data.detail && typeof data.detail === "object" && typeof data.detail.code === "string") {
    return data.detail.code;
  }
  return null;
};

// Pull the hint string out of an API error. Returns null when none set.
export const errorHint = (e) => {
  const data = e?.response?.data;
  if (!data) return null;
  if (data.error_meta && typeof data.error_meta === "object" && typeof data.error_meta.hint === "string") {
    return data.error_meta.hint;
  }
  if (data.detail && typeof data.detail === "object" && typeof data.detail.hint === "string") {
    return data.detail.hint;
  }
  return null;
};
